export enum GroupByOption {
    Hierarchy = "Hierarchy",
    Agent = "Agent",
    AgentManager = "AgentManager",
    ConversationLabeling = "ConversationLabeling",
    EvaluationType = "EvaluationType",
    Language = "Language",
    MediaType = "MediaType",
    // This value is persisted user data and still reads the legacy Themes-via-Topics path.
    PrimaryThemes = "PrimaryThemes",
    Themes = "Themes",
    Outline = "Outline",
    PrimaryTopic = "PrimaryTopic",
    PrimaryDriver = "PrimaryDriver",
    PatientJourney = "PatientJourney",
    EddyEffect = "EddyEffect",
    CallDirection = "CallDirection",
    ContactType = "ContactType",
    Classifiers = "Classifiers",
    ModelResults = "ModelResults",
    EndingSentiment = "EndingSentiment",
    StartingSentiment = "StartingSentiment",
    HipaaCompliance = "HipaaCompliance",
    SafetyEvent = "SafetyEvent",
    ExtendedMetadata = "ExtendedMetadata",
    QuestionResult = "QuestionResult",
    Date = "Date",
    CallDuration = "CallDuration",
    MessageCount = "MessageCount",
    ModuleScore = "ModuleScore",
}

/**
 * The option as a plain string union. Grouping options are persisted by name in vizOptions and
 * exchanged with the backend as strings, so this -- not the enum -- is the type to compare and
 * narrow against.
 */
export type GroupByOptionValue = `${GroupByOption}`;

/**
 * What a dimension is grouped by, which decides the configuration it carries and the editor that
 * configures it: a set of chosen values, a date grain, or numeric bucket boundaries.
 */
export type GroupByKind = "categorical" | "date" | "numericRange";

/** `auto` defers the choice to the backend, which picks from the request's date range. */
export type DateGroupByGrain = "auto" | "day" | "week" | "month" | "quarter";

/**
 * Every option belongs to exactly one kind, and the kind decides which configuration fields the
 * option carries and which editor configures it. This map is the frontend source of truth; the
 * backend catalog decides which options are *available*, not what shape they have.
 *
 * `as const satisfies` rather than a type annotation: `satisfies` still requires an entry for every
 * option and a valid kind for each, while `as const` keeps the literal kinds so the per-kind option
 * types below can be derived from this map instead of restated alongside it.
 */
export const groupByKindByOption = {
    Hierarchy: "categorical",
    Agent: "categorical",
    AgentManager: "categorical",
    ConversationLabeling: "categorical",
    EvaluationType: "categorical",
    Language: "categorical",
    MediaType: "categorical",
    PrimaryThemes: "categorical",
    Themes: "categorical",
    Outline: "categorical",
    PrimaryTopic: "categorical",
    PrimaryDriver: "categorical",
    PatientJourney: "categorical",
    EddyEffect: "categorical",
    CallDirection: "categorical",
    ContactType: "categorical",
    Classifiers: "categorical",
    ModelResults: "categorical",
    EndingSentiment: "categorical",
    StartingSentiment: "categorical",
    HipaaCompliance: "categorical",
    SafetyEvent: "categorical",
    ExtendedMetadata: "categorical",
    QuestionResult: "categorical",
    Date: "date",
    CallDuration: "numericRange",
    MessageCount: "numericRange",
    ModuleScore: "numericRange",
} as const satisfies Record<GroupByOptionValue, GroupByKind>;

/**
 * The options of one kind, read off `groupByKindByOption`. Derived rather than listed so a new
 * option's kind is stated once: giving it the wrong kind is then a type error at the point of use,
 * not a disagreement between a hand-written list and the map the runtime guards read.
 */
type OptionsOfKind<TKind extends GroupByKind> = {
    [TOption in GroupByOptionValue]: (typeof groupByKindByOption)[TOption] extends TKind
        ? TOption
        : never;
}[GroupByOptionValue];

export type DateGroupByOption = OptionsOfKind<"date">;
export type NumericRangeGroupByOption = OptionsOfKind<"numericRange">;
export type CategoricalGroupByOption = OptionsOfKind<"categorical">;
export type ContinuousGroupByOption =
    | DateGroupByOption
    | NumericRangeGroupByOption;

/**
 * Whether values between a range's units exist, which is what decides where its bucket boundaries
 * fall and how its buckets read.
 *
 * `continuous` means values in between are meaningful, so consecutive buckets share a boundary and the
 * boundary value belongs to the bucket it opens. Cut points 2, 5, 10 describe [0, 2), [2, 5),
 * [5, 10), [10, ∞), and a call of exactly two minutes is in the second.
 *
 * `discrete` means there is nothing in between, so buckets share no boundary: both ends of one are
 * inclusive and the next starts a whole unit later. Cut points 5, 10, 20 describe 0-5, 6-10, 11-20
 * and 21+. On whole numbers that is the same partition a continuous range cut at 6, 11, 21 would
 * describe. The mode is about which reads correctly to a user, since "0-5 messages" then
 * "6-10 messages" is how people count things, rather than about which rows land where.
 */
export type NumericRangeMode = "continuous" | "discrete";

/**
 * The first value in the bucket a cut point opens, in the cut point's own unit. This is the one
 * place the two modes differ, and what every other difference between them follows from.
 *
 * A continuous bucket starts at the cut point below it, since that value is the boundary the two
 * share; a discrete one starts a whole unit past it, since they share nothing. The same answer
 * labels a row's read-only lower bound in the editor, names the trailing "31+" row, and starts the
 * span a drilldown out of the bucket filters on, so none of the three can disagree with the chart.
 *
 * The arithmetic lives here but the mode does not: the catalog publishes it as
 * `numericRangeMode`, from the same definition the bucket labels and the bucketing SQL are written
 * from, so the two cannot disagree about which dimensions are discrete.
 */
export const nextBucketStart = (
    cutPoint: number,
    mode: NumericRangeMode,
): number => (mode === "discrete" ? cutPoint + 1 : cutPoint);

export type CategoricalGroupByConfiguration = {
    kind: "categorical";
    option: CategoricalGroupByOption;
    /** The chosen values, as the ids the backend filters on -- not display labels. */
    valueIds: string[];
    /** Model Results only: which model's results `valueIds` belong to. */
    llmClassifierId?: string;
    /** Extended Metadata only: which metadata field `valueIds` are values of. */
    extendedMetadataKey?: string;
    /**
     * Question Result only: which question's answer options `valueIds` are the tag ids of.
     * Required, along with `licensedModuleId`, for a Question Result grouping to read back.
     */
    questionId?: string;
    /**
     * Question Result only: the module the question belongs to. Required, though the question alone
     * determines it, because the dashboard's permission pass keys on it to hide charts grouped by a
     * module the viewer cannot see. `normalizeGroupByConfiguration` rejects a persisted grouping
     * without one, so it never reaches that pass as an unclassifiable chart.
     */
    licensedModuleId?: string;
};

export type DateGroupByConfiguration = {
    kind: "date";
    option: DateGroupByOption;
    grain: DateGroupByGrain;
};

export type NumericRangeGroupByConfiguration = {
    kind: "numericRange";
    option: NumericRangeGroupByOption;
    /**
     * Interior bucket boundaries, strictly increasing, in the unit the backend catalog publishes
     * for the option.
     *
     * n boundaries make n+1 buckets, the first and last unbounded. Where one bucket ends and the
     * next begins is the option's `NumericRangeMode`: boundaries 2, 5, 10 describe [0, 2), [2, 5),
     * [5, 10) and [10, ∞) for a continuous range, and 0-2, 3-5, 6-10 and 11+ for a discrete one.
     * Buckets themselves are derived, never persisted.
     */
    cutPoints: number[];
    /** Module Score only: which module's score is being bucketed. */
    moduleId?: string;
};

/**
 * A chart's grouping, as persisted in vizOptions under `groupBy` and sent to the backend. The
 * `kind` discriminant is what makes the extra fields readable, and it always agrees with the
 * option's entry in `groupByKindByOption` -- readers reject configurations where it does not.
 */
export type GroupByConfiguration =
    | CategoricalGroupByConfiguration
    | DateGroupByConfiguration
    | NumericRangeGroupByConfiguration;

/**
 * A range as the configuration drawer edits it, where a boundary may not have been typed yet.
 *
 * `null` is that pending boundary: what "+ Add Group" leaves behind, and what clearing a value
 * returns a row to. It is deliberately part of the shape rather than component state, because the
 * drawer derives its editor from vizOptions on every render -- a pending row held anywhere else
 * would vanish on the next keystroke elsewhere in the range.
 *
 * It never reaches a saved chart: `validateGroupByDraft` blocks Save while one is present, and
 * `normalizeGroupByConfiguration` rejects a configuration carrying one, so the request-time reader
 * still only ever sees complete `number[]` cut points.
 */
export type NumericRangeGroupByDraft = Omit<
    NumericRangeGroupByConfiguration,
    "cutPoints"
> & {
    cutPoints: (number | null)[];
};

/**
 * A chart's grouping as the drawer edits it -- `GroupByConfiguration` except that a numeric range
 * may hold a pending boundary. This is what vizOptions is typed to hold, since the drawer writes
 * its working copy back there; see `groupByDraft` for why editing needs a looser shape than
 * requesting does.
 */
export type GroupByDraftConfiguration =
    | CategoricalGroupByConfiguration
    | DateGroupByConfiguration
    | NumericRangeGroupByDraft;

export const dateGroupByGrains: DateGroupByGrain[] = [
    "auto",
    "day",
    "week",
    "month",
    "quarter",
];

export const isGroupByOption = (value: unknown): value is GroupByOptionValue =>
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(groupByKindByOption, value);

export const getGroupByKind = (option: GroupByOptionValue): GroupByKind =>
    groupByKindByOption[option];

export const isCategoricalGroupByOption = (
    value: unknown,
): value is CategoricalGroupByOption =>
    isGroupByOption(value) && getGroupByKind(value) === "categorical";

export const isDateGroupByOption = (
    value: unknown,
): value is DateGroupByOption =>
    isGroupByOption(value) && getGroupByKind(value) === "date";

export const isNumericRangeGroupByOption = (
    value: unknown,
): value is NumericRangeGroupByOption =>
    isGroupByOption(value) && getGroupByKind(value) === "numericRange";

export const isDateGroupByGrain = (value: unknown): value is DateGroupByGrain =>
    typeof value === "string" &&
    dateGroupByGrains.includes(value as DateGroupByGrain);

/** Shared with the two grouping readers (`groupByDraft`, `normalizeGroupByConfiguration`) so a
 * plain-object check can't quietly diverge between them. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** Shared with the two grouping readers; trims and treats a blank string as absent. */
export const toOptionalString = (value: unknown): string | undefined =>
    typeof value === "string" && value.trim().length > 0
        ? value.trim()
        : undefined;

/**
 * Whether cut points are strictly increasing, which is what makes them describe non-empty,
 * non-overlapping buckets. Shared so the strict reader and the draft validator apply one rule
 * rather than two copies of it; the backend's `HasValidCutPoints` is the mirror of this.
 */
export const isStrictlyIncreasing = (values: number[]): boolean =>
    values.every((value, index) => index === 0 || values[index - 1] < value);
